import { useContext, useState, useEffect } from "react";
import { YearContext, AuthContext } from "../../context/context";
import {
  TbChevronDown,
  TbBell,
  TbHelp,
  TbArrowLeft,
  TbLogout,
  TbCalendarMonth,
} from "react-icons/tb";
import { useLocation, useNavigate } from "react-router-dom";
import BtnPrimaryL from "../Buttons/BtnPrimaryL";
import IconCircle from "../Elements/IconCircle";
import Dropdown from "../Elements/Dropdown";
import { PiCalendarHeartDuotone } from "react-icons/pi";
import { GrMoney } from "react-icons/gr";
import { useLanguage } from "../../context/LanguageContext";
import { useNotifications } from "../../context/NotificationContext";
import PropTypes from "prop-types";

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

export default function Nav({ 
  setSidebarToggled,
  // Finance-specific props
  selectedMonth,
  selectedCurrency,
  onMonthChange,
  onCurrencyChange,
  allCurrencies,
  authUserData
}) {
  const { selectedYear, setSelectedYear, years, loading, error } =
    useContext(YearContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const location = useLocation();
  const { userData, logout } = useContext(AuthContext);
  const { copy } = useLanguage();
  const isMobile = useIsMobile();

  const navigate = useNavigate();
  const { unreadCount } = useNotifications();

  const goToNotifications = () => {
    navigate("/notifications");
  };

  const hideCalendarYear =
    ["/calendar", "/settings", "/pricing", "/notifications"].includes(
      location.pathname
    ) ||
    location.pathname.startsWith("/goals/") ||
    !userData?.goals?.some((goal) => goal.year) ||
    userData?.goals?.length === 0;

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setIsDropdownOpen(false);
  };

  const handleMonthChange = (month) => {
    onMonthChange && onMonthChange(month);
    setIsMonthDropdownOpen(false);
  };

  const handleCurrencyChange = (currency) => {
    onCurrencyChange && onCurrencyChange(currency);
    setIsCurrencyDropdownOpen(false);
  };

  // Show finance controls only on /finance page AND if user has goals with a year
  const hasGoalsWithYear = userData?.goals && userData.goals.length > 0 && userData.goals.some(goal => goal.year);
  const showFinanceControls = location.pathname === "/finance" && hasGoalsWithYear;

  // Month options for finance controls
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Currency options for finance controls
  const currencyOptions = authUserData?.currencies || allCurrencies || [
    "EUR", "USD", "PLN", "DKK", "GBP", "CAD", "AUD", "CHF", "JPY", "SEK", "NOK"
  ];

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 border-bottom-base flex justify-between md:sticky top-0 z-20 navigation min-h-15 background-page-color">
      <div className="flex items-center justify-center gap-4">
        {isMobile && (
          <button
            onClick={() => navigate("/")}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <img src="/niumi-logo-n.svg" alt="Niumi" className="w-8 h-8" />
          </button>
        )}
        {location.pathname.startsWith("/goals/") ? (
          isMobile ? (
            <IconCircle icon={TbArrowLeft} onClick={() => navigate("/goals")} />
          ) : (
            <button
              onClick={() => navigate("/goals")}
              className="flex items-center gap-1 text-sm cursor-pointer"
            >
              <TbArrowLeft size={14} />
              <span>All goals</span>
            </button>
          )
        ) : (
          !hideCalendarYear && (
            <div className="flex items-center justify-center gap-1 bg-white pl-3 pr-1 py-1 rounded-full card-base-border">
              <PiCalendarHeartDuotone size={24} />

              <div className="flex gap-1 items-center">
                {loading ? (
                  <p>Loading...</p>
                ) : error ? (
                  <p>Error: {error}</p>
                ) : (
                  <>
                    <span>{selectedYear}</span>
                    <Dropdown
                      icon={TbChevronDown}
                      rotateOnOpen={true}
                      isOpen={isDropdownOpen}
                      onOpenChange={setIsDropdownOpen}
                      selectedValue={selectedYear}
                      options={years.map((year) => ({
                        text: year,
                        onClick: () => handleYearChange(year),
                      }))}
                    />
                  </>
                )}
              </div>
            </div>
          )
        )}

        {/* Finance Controls - shown only on /finance page */}
        {showFinanceControls && (
          <div className="hidden md:flex flex-row items-center gap-4">
            {/* Month Selector */}
            <div className="flex items-center justify-center gap-1 bg-white pl-3 pr-1 py-1 rounded-full card-base-border">
              <TbCalendarMonth size={24} />
              <div className="flex gap-1 items-center">
                <span>{selectedMonth}</span>
                <Dropdown
                  icon={TbChevronDown}
                  rotateOnOpen={true}
                  isOpen={isMonthDropdownOpen}
                  onOpenChange={setIsMonthDropdownOpen}
                  selectedValue={selectedMonth}
                  options={months.map((month) => ({
                    text: month,
                    onClick: () => handleMonthChange(month),
                  }))}
                />
              </div>
            </div>

            {/* Currency Selector */}
            <div className="flex items-center justify-center gap-1 bg-white pl-3 pr-1 py-1 rounded-full card-base-border">
              <GrMoney size={20} />
              <div className="flex gap-1 items-center">
                <span>{selectedCurrency || "Select currency"}</span>
                <Dropdown
                  icon={TbChevronDown}
                  rotateOnOpen={true}
                  isOpen={isCurrencyDropdownOpen}
                  onOpenChange={setIsCurrencyDropdownOpen}
                  selectedValue={selectedCurrency}
                  options={currencyOptions.map((currency) => ({
                    text: currency,
                    onClick: () => handleCurrencyChange(currency),
                  }))}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-3">
          <div className="relative">
            <IconCircle onClick={goToNotifications} icon={TbBell} />
            {unreadCount > 0 && (
              <span className="absolute -bottom-3 right-0.5 transform -translate-y-1/2 translate-x-1/2 bg-primary !text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border border-white">
                {unreadCount}
              </span>
            )}
          </div>
          <IconCircle link="https://www.niumi.dk/help-center/" icon={TbHelp} />
          {/* <LanguageSwitcher /> */}
          {isMobile && <IconCircle onClick={logout} icon={TbLogout} />}
        </div>
        <div className="hidden md:block">
          <BtnPrimaryL
            buttonCTA={copy.navigation.button}
            onClickLink={() =>
              setSidebarToggled ? setSidebarToggled(true) : null
            }
          />
        </div>
        {/* Burger button for mobile */}
      </div>
    </div>
  );
}

Nav.propTypes = {
  setSidebarToggled: PropTypes.func.isRequired,
  // Finance-specific props
  selectedMonth: PropTypes.string,
  selectedCurrency: PropTypes.string,
  onMonthChange: PropTypes.func,
  onCurrencyChange: PropTypes.func,
  allCurrencies: PropTypes.array,
  authUserData: PropTypes.object,
};
